import { useState } from 'react';
import CustomTooltip, { CustomTooltipTrigger } from './CustomTooltip';
import { CircleInfoLightGlyph } from '@acast-tech/decibel-glyphs';
import { twMerge } from 'tailwind-merge';

interface TextTooltipProps {
  popoverRef: React.RefObject<HTMLDivElement>;
  title?: string;
  description: string;
  buttonText?: string;
  onMouseOver?: () => void;
}

/**
 * This is the text tooltip component.
 * It is used to display a tooltip with a title and description.
 *
 * @param popoverRef - The popover ref.
 * @param title - The title of the tooltip.
 * @param description - The description of the tooltip.
 * @param buttonText - The text of the button. If not provided, the button just displays the info glyph.
 * @param onMouseOver - The function to call when the mouse is over the tooltip.
 * @returns The text tooltip component.
 */
const TextTooltip = ({ popoverRef, title, description, buttonText, onMouseOver }: TextTooltipProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  return (
    <div className="relative" ref={popoverRef} onMouseOver={onMouseOver}>
      <CustomTooltip
        className="bottom-[5px] right-[-50px] rounded-[4px] p-4"
        isOpen={isTooltipOpen}
        popoverRef={popoverRef}
        zIndex={50}
      >
        {title && <h6 className="font-bold mb-2">{title}</h6>}
        <p>{description}</p>
      </CustomTooltip>
      <CustomTooltipTrigger
        className={twMerge(buttonText && 'flex items-center gap-2')}
        setIsOpen={setIsTooltipOpen}
        popoverRef={popoverRef}
        ariaExpanded={isTooltipOpen}
        ariaLabel={`${title} tooltip`}
      >
        {buttonText!}
        <CircleInfoLightGlyph className="w-3 h-3 fill-textMedGrey" />
      </CustomTooltipTrigger>
    </div>
  );
};

export default TextTooltip;
